import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    url: `https://baoyutv.com/article/${article.slug}`,
    author: {
      '@type': 'Organization',
      name: 'BaoyuTV',
    },
    publisher: {
      '@type': 'Organization',
      name: 'BaoyuTV',
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - BaoyuTV教程资讯`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://baoyutv.com/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogImage={article.image}
        schema={articleSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 pt-10 pb-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: article.category, path: `/article/${articles[0].slug}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 pb-16">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                  {article.category}
                </span>
                <span className="text-sm text-foreground-400">{article.date}</span>
                <span className="text-sm text-foreground-400">{article.readTime} 阅读</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-2.5 py-1 rounded-md bg-background-100 text-foreground-500 border border-background-200/70">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-10 rounded-2xl overflow-hidden h-64 md:h-80">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: article.content
                  .replace(/<h2/g, '<h2 class="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4"')
                  .replace(/<h3/g, '<h3 class="font-heading text-lg font-semibold text-foreground-800 mt-8 mb-3"')
                  .replace(/<p>/g, '<p class="text-foreground-600 mb-4 leading-relaxed">'),
              }}
            />

            {relatedArticles.length > 0 && (
              <div className="mt-16 pt-10 border-t border-background-200/70">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.slug}
                      to={`/article/${rel.slug}`}
                      className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="h-40 overflow-hidden">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {rel.title}
                        </h4>
                        <span className="text-xs text-foreground-400 mt-2 block">{rel.date}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}